import { useState, type ComponentType } from 'react';
import { useAvailableUpdate, useMachineState, useSyncStatus, useTabletSetup } from './app/context';
import { Button } from './components/Button';
import { Stage } from './components/Stage';
import type { Screen } from './domain/types';
import { useWakeLock } from './hooks/useWakeLock';
import { AbortedScreen } from './screens/AbortedScreen';
import { BedScreen } from './screens/BedScreen';
import { BootScreen } from './screens/BootScreen';
import { ConnectScreen } from './screens/ConnectScreen';
import { CountdownScreen } from './screens/CountdownScreen';
import { CycleScreen } from './screens/CycleScreen';
import { LoginScreen } from './screens/LoginScreen';
import { PositionScreen } from './screens/PositionScreen';
import { ReportScreen } from './screens/ReportScreen';
import { ResetTabletScreen, SetupScreen } from './screens/SetupScreen';
import setupStyles from './screens/SetupScreen.module.css';
import { WardScreen } from './screens/WardScreen';
import { WelcomeScreen } from './screens/WelcomeScreen';

const SCREENS: Readonly<Record<Screen, ComponentType>> = {
  boot: BootScreen,
  login: LoginScreen,
  welcome: WelcomeScreen,
  connect: ConnectScreen,
  ward: WardScreen,
  bed: BedScreen,
  position: PositionScreen,
  countdown: CountdownScreen,
  cycle: CycleScreen,
  aborted: AbortedScreen,
  report: ReportScreen,
};

export function App() {
  const { screen } = useMachineState();
  const sync = useSyncStatus();
  const setup = useTabletSetup();
  const update = useAvailableUpdate();
  const [managing, setManaging] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  useWakeLock(screen === 'countdown' || screen === 'cycle');

  // Offline mode: a tablet drives the robot it was set up for, so a new (or reset) tablet starts
  // with setup; nobody can sign in before.
  if (setup && sync && (!sync.enrolled || settingUp)) {
    return (
      <Stage>
        <SetupScreen onHold={setSettingUp} />
      </Stage>
    );
  }
  if (setup && managing && screen === 'login') {
    return (
      <Stage>
        <ResetTabletScreen onClose={() => setManaging(false)} />
      </Stage>
    );
  }
  const Current = SCREENS[screen];
  return (
    <Stage>
      <Current />
      {screen === 'login' && (setup || update) && (
        <div className={setupStyles.setupLinks}>
          {/* Opens the install page in the browser: the download, then Android's Install. */}
          {update && (
            <a
              className={setupStyles.updateLink}
              href={update.url}
              target="_blank"
              rel="noreferrer"
            >
              UPDATE AVAILABLE · {update.version}
            </a>
          )}
          {setup && (
            <Button variant="ghost" size="xs" onClick={() => setManaging(true)}>
              TABLET SETUP
            </Button>
          )}
        </div>
      )}
    </Stage>
  );
}
